class MultiFlavorSelector {
  constructor() {
    this.flavorSelector = document.querySelector('.flavor-selector');
    if (!this.flavorSelector) return;

    this.productId = this.flavorSelector.dataset.productId;
    this.variants = new Map();
    this.cart = new Map();
    this.carousels = new Map();
    this.shopifyCartCount = 0;

    this.initElements();
    this.initCarousels();
    this.initEventListeners();
    this.loadCartFromStorage();
    this.checkShopifyCart();
    this.updateDisplay();
  }

  initElements() {
    // Get all flavor cards and their data
    this.flavorCards = this.flavorSelector.querySelectorAll('.flavor-card');
    this.flavorCards.forEach(card => {
      const variantId = card.dataset.variantId;
      const variantPrice = parseInt(card.dataset.variantPrice);
      this.variants.set(variantId, {
        id: variantId,
        price: variantPrice,
        element: card,
        quantityInput: card.querySelector('.quantity-input'),
        minusBtn: card.querySelector('.quantity-btn--minus'),
        plusBtn: card.querySelector('.quantity-btn--plus')
      });
    });

    // Cart summary elements
    this.totalPriceElement = document.querySelector('[data-total-price]');
    this.totalCountElement = document.querySelector('[data-total-count]');
    this.addToCartBtn = document.querySelector('[data-add-to-cart]');
    this.buyNowBtn = document.querySelector('[data-buy-now]');

    // Quick select buttons
    this.quickSelectBtns = document.querySelectorAll('.quick-select__btn');
  }

  initCarousels() {
    // Initialize carousel functionality for each flavor card
    this.flavorCards.forEach(card => {
      const carouselContainer = card.querySelector('.flavor-card__image-carousel');
      if (!carouselContainer) return;

      const variantId = card.dataset.variantId;
      const slides = carouselContainer.querySelectorAll('.carousel-slide');
      const dots = carouselContainer.querySelectorAll('.carousel-dot');
      
      if (slides.length <= 1) return; // No need for carousel with single image

      const carousel = {
        container: carouselContainer,
        slides: slides,
        dots: dots,
        currentSlide: 0,
        isAnimating: false
      };

      this.carousels.set(variantId, carousel);

      // Add dot click listeners
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(variantId, index);
        });
      });

      // Add keyboard navigation
      carouselContainer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.previousSlide(variantId);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.nextSlide(variantId);
        }
      });

      // Add touch/swipe support
      this.addTouchSupport(variantId, carouselContainer);

      // Add auto-advance on hover (optional)
      if (slides.length === 3) {
        this.addAutoAdvance(variantId, carouselContainer);
      }
    });
  }

  goToSlide(variantId, slideIndex) {
    const carousel = this.carousels.get(variantId);
    if (!carousel || carousel.isAnimating || slideIndex === carousel.currentSlide) return;

    carousel.isAnimating = true;

    // Update slides
    carousel.slides[carousel.currentSlide].classList.remove('active');
    carousel.slides[slideIndex].classList.add('active');

    // Update dots
    carousel.dots[carousel.currentSlide].classList.remove('active');
    carousel.dots[slideIndex].classList.add('active');

    carousel.currentSlide = slideIndex;

    // Reset animation flag
    setTimeout(() => {
      carousel.isAnimating = false;
    }, 300);

    // Add active state to card for touch hint removal
    carousel.container.closest('.flavor-card').classList.add('carousel-active');
  }

  nextSlide(variantId) {
    const carousel = this.carousels.get(variantId);
    if (!carousel) return;

    const nextIndex = (carousel.currentSlide + 1) % carousel.slides.length;
    this.goToSlide(variantId, nextIndex);
  }

  previousSlide(variantId) {
    const carousel = this.carousels.get(variantId);
    if (!carousel) return;

    const prevIndex = carousel.currentSlide === 0 
      ? carousel.slides.length - 1 
      : carousel.currentSlide - 1;
    this.goToSlide(variantId, prevIndex);
  }

  addTouchSupport(variantId, container) {
    let startX = 0;
    let endX = 0;
    let startY = 0;
    let endY = 0;

    container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      
      const deltaX = startX - endX;
      const deltaY = startY - endY;
      
      // Only trigger if horizontal swipe is more significant than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          // Swipe left - next slide
          this.nextSlide(variantId);
        } else {
          // Swipe right - previous slide
          this.previousSlide(variantId);
        }
      }
    }, { passive: true });
  }

  addAutoAdvance(variantId, container) {
    const carousel = this.carousels.get(variantId);
    if (!carousel) return;

    let autoAdvanceInterval;
    let hasUserInteracted = false;

    const startAutoAdvance = () => {
      if (hasUserInteracted) return;
      
      autoAdvanceInterval = setInterval(() => {
        if (!hasUserInteracted && !carousel.isAnimating) {
          this.nextSlide(variantId);
        }
      }, 3000);
    };

    const stopAutoAdvance = () => {
      if (autoAdvanceInterval) {
        clearInterval(autoAdvanceInterval);
        autoAdvanceInterval = null;
      }
    };

    const markUserInteraction = () => {
      hasUserInteracted = true;
      stopAutoAdvance();
    };

    // Start auto-advance on hover
    container.addEventListener('mouseenter', startAutoAdvance);
    container.addEventListener('mouseleave', stopAutoAdvance);

    // Stop auto-advance on user interaction
    container.addEventListener('click', markUserInteraction);
    container.addEventListener('touchstart', markUserInteraction);
    container.addEventListener('keydown', markUserInteraction);
  }

  initEventListeners() {
    // Quantity button listeners
    this.variants.forEach(variant => {
      // Plus button
      variant.plusBtn.addEventListener('click', () => {
        this.updateQuantity(variant.id, 1);
      });

      // Minus button
      variant.minusBtn.addEventListener('click', () => {
        this.updateQuantity(variant.id, -1);
      });

      // Input change
      variant.quantityInput.addEventListener('change', (e) => {
        const newQuantity = Math.max(0, Math.min(10, parseInt(e.target.value) || 0));
        this.setQuantity(variant.id, newQuantity);
      });

      // Input blur to ensure valid values
      variant.quantityInput.addEventListener('blur', (e) => {
        const quantity = this.cart.get(variant.id) || 0;
        e.target.value = quantity;
      });
    });

    // Quick select buttons
    this.quickSelectBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const preset = btn.dataset.preset;
        this.applyPreset(preset);
      });
    });

    // Add to cart button
    this.addToCartBtn.addEventListener('click', () => {
      this.addToCart();
    });

    // Buy now button
    this.buyNowBtn.addEventListener('click', () => {
      this.buyNow();
    });

    // Save cart state on page unload
    window.addEventListener('beforeunload', () => {
      this.saveCartToStorage();
    });
  }

  updateQuantity(variantId, change) {
    const currentQty = this.cart.get(variantId) || 0;
    const newQty = Math.max(0, Math.min(10, currentQty + change));
    this.setQuantity(variantId, newQty);
  }

  setQuantity(variantId, quantity) {
    const validQuantity = Math.max(0, Math.min(10, quantity));
    
    if (validQuantity === 0) {
      this.cart.delete(variantId);
    } else {
      this.cart.set(variantId, validQuantity);
    }

    this.updateDisplay();
    this.saveCartToStorage();
  }

  updateDisplay() {
    let totalPrice = 0;
    let totalCount = 0;

    // Update each flavor card
    this.variants.forEach(variant => {
      const quantity = this.cart.get(variant.id) || 0;
      const price = variant.price;

      // Update quantity input
      variant.quantityInput.value = quantity;

      // Update button states
      variant.minusBtn.disabled = quantity <= 0;
      variant.plusBtn.disabled = quantity >= 10;

      // Update card visual state
      if (quantity > 0) {
        variant.element.classList.add('has-quantity');
        totalPrice += price * quantity;
        totalCount += quantity;
      } else {
        variant.element.classList.remove('has-quantity');
      }
    });

    // Update cart summary (if elements exist)
    if (this.totalPriceElement) {
      this.totalPriceElement.textContent = this.formatMoney(totalPrice);
    }
    if (this.totalCountElement) {
      this.totalCountElement.textContent = `(${totalCount} item${totalCount !== 1 ? 's' : ''})`;
    }

    // Update button states
    const hasItems = totalCount > 0;
    this.addToCartBtn.disabled = !hasItems;
    // Checkout button enabled if items selected OR items already in Shopify cart
    this.buyNowBtn.disabled = !hasItems && this.shopifyCartCount === 0;

    // Update mobile sticky bar if it exists
    this.updateMobileStickyBar(totalPrice, totalCount, hasItems);

    // Add pulse animation to cart summary when it changes
    if (hasItems && this.totalPriceElement) {
      this.totalPriceElement.classList.add('pulse');
      setTimeout(() => {
        this.totalPriceElement.classList.remove('pulse');
      }, 300);
    }
  }
  
  updateMobileStickyBar(totalPrice, totalCount, hasItems) {
    const stickyBar = document.querySelector('.mobile-sticky-bar');
    if (!stickyBar) return;
    
    // Update sticky bar totals
    const stickyTotalPrice = stickyBar.querySelector('[data-total-price]');
    const stickyTotalCount = stickyBar.querySelector('[data-total-count]');
    const stickyAddToCartBtn = stickyBar.querySelector('[data-add-to-cart]');
    const stickyBuyNowBtn = stickyBar.querySelector('[data-buy-now]');
    
    if (stickyTotalPrice) {
      stickyTotalPrice.textContent = this.formatMoney(totalPrice);
    }
    
    if (stickyTotalCount) {
      stickyTotalCount.textContent = `(${totalCount} item${totalCount !== 1 ? 's' : ''})`;
    }
    
    if (stickyAddToCartBtn) {
      stickyAddToCartBtn.disabled = !hasItems;
    }
    
    if (stickyBuyNowBtn) {
      stickyBuyNowBtn.disabled = !hasItems;
    }
  }

  applyPreset(preset) {
    const presets = {
      sample: 1,
      stock: 2,
      party: 3
    };

    const quantity = presets[preset] || 1;
    
    this.variants.forEach(variant => {
      this.setQuantity(variant.id, quantity);
    });

    // Visual feedback
    const btn = document.querySelector(`[data-preset="${preset}"]`);
    btn.classList.add('pulse');
    setTimeout(() => {
      btn.classList.remove('pulse');
    }, 300);
  }

  async addToCart() {
    if (this.cart.size === 0) return;

    this.setLoading(true);

    try {
      const items = Array.from(this.cart.entries()).map(([variantId, quantity]) => ({
        id: variantId,
        quantity: quantity
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }

      const result = await response.json();

      // Calculate total items added
      const totalItemsAdded = Array.from(this.cart.values()).reduce((sum, qty) => sum + qty, 0);

      // Update cart count in header if it exists
      await this.updateCartCount();

      // Clear local cart after successful add
      this.clearCart();

      // Show checkout modal
      this.showCheckoutModal(totalItemsAdded);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorFeedback('Failed to add items to cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async buyNow() {
    // If items are selected on this page, add them to cart first
    if (this.cart.size > 0) {
      await this.addToCart();
    }

    // Redirect to checkout after a brief delay
    setTimeout(() => {
      window.location.href = '/checkout';
    }, 500);
  }

  setLoading(loading) {
    this.flavorSelector.classList.toggle('loading', loading);
    this.addToCartBtn.disabled = loading || this.cart.size === 0;
    this.buyNowBtn.disabled = loading || (this.cart.size === 0 && this.shopifyCartCount === 0);

    if (loading) {
      this.addToCartBtn.textContent = 'Adding...';
      this.buyNowBtn.textContent = 'Processing...';
    } else {
      this.addToCartBtn.textContent = 'Add to Cart';
      this.buyNowBtn.textContent = 'Checkout';
    }
  }

  showSuccessFeedback() {
    this.addToCartBtn.classList.add('added-to-cart');
    this.addToCartBtn.textContent = 'Added to Cart!';
    
    setTimeout(() => {
      this.addToCartBtn.classList.remove('added-to-cart');
      this.addToCartBtn.textContent = 'Add to Cart';
    }, 2000);
  }

  showErrorFeedback(message) {
    // You could implement a toast notification here
    alert(message);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count in header
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });

      // Update cart icon bubble (the badge on the cart icon)
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        // Fetch updated cart icon HTML
        const bubbleResponse = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
        const bubbleHtml = await bubbleResponse.text();

        // Create a temporary element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = bubbleHtml;

        // Get the new content (skip the first line which is just whitespace/script tag)
        const newContent = tempDiv.querySelector('.cart-count-bubble') || tempDiv.textContent.trim();

        // Update the cart icon content
        const existingBubble = cartIconBubble.querySelector('.cart-count-bubble');
        if (newContent && typeof newContent !== 'string') {
          // If there's an existing bubble, replace it
          if (existingBubble) {
            existingBubble.replaceWith(newContent);
          } else {
            // Otherwise append the new bubble
            cartIconBubble.appendChild(newContent.cloneNode(true));
          }
        } else if (!newContent && existingBubble) {
          // Remove bubble if cart is empty
          existingBubble.remove();
        }
      }

      return cart.item_count;
    } catch (error) {
      console.error('Error updating cart count:', error);
      return 0;
    }
  }

  async checkShopifyCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.shopifyCartCount = cart.item_count;
      this.updateDisplay();
    } catch (error) {
      console.error('Error checking Shopify cart:', error);
      this.shopifyCartCount = 0;
    }
  }

  showCheckoutModal(itemCount) {
    // Dispatch event to open the checkout modal
    document.dispatchEvent(new CustomEvent('checkout-modal:open', {
      detail: { itemCount }
    }));

    // Also dispatch cart updated event for other listeners
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  clearCart() {
    this.cart.clear();
    this.updateDisplay();
    this.saveCartToStorage();
  }

  saveCartToStorage() {
    try {
      const cartData = Object.fromEntries(this.cart);
      localStorage.setItem('preorder-cart', JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving cart to storage:', error);
    }
  }

  loadCartFromStorage() {
    try {
      const cartData = localStorage.getItem('preorder-cart');
      if (cartData) {
        const parsed = JSON.parse(cartData);
        this.cart = new Map(Object.entries(parsed));
        
        // Debug: log what was loaded
        console.log('Loaded cart from storage:', this.cart);
        
        // Validate cart data - clear if it seems corrupted
        let totalItems = 0;
        this.cart.forEach(quantity => totalItems += parseInt(quantity) || 0);
        
        if (totalItems > 50) { // Unreasonable number of items
          console.warn('Cart data seems corrupted, clearing...');
          this.clearCart();
        }
      }
    } catch (error) {
      console.error('Error loading cart from storage:', error);
      this.cart = new Map();
      // Clear corrupted localStorage
      localStorage.removeItem('preorder-cart');
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
}

// Mobile sticky bar functionality
class MobileStickyBar {
  constructor() {
    this.isVisible = false;
    this.heroHeight = 0;
    this.init();
  }

  init() {
    if (window.innerWidth <= 749) {
      this.createStickyBar();
      this.setupScrollListener();
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth <= 749 && !this.stickyBar) {
        this.createStickyBar();
        this.setupScrollListener();
      } else if (window.innerWidth > 749 && this.stickyBar) {
        this.removeStickyBar();
        this.removeScrollListener();
      }
    });
  }
  
  setupScrollListener() {
    // Get hero section height
    const heroSection = document.querySelector('.paradise-hero');
    this.heroHeight = heroSection ? heroSection.offsetHeight : window.innerHeight;
    
    // Initially hide the sticky bar
    if (this.stickyBar) {
      this.stickyBar.style.transform = 'translateY(100%)';
      this.isVisible = false;
    }
    
    // Add scroll listener
    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }
  
  removeScrollListener() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }
  
  handleScroll() {
    if (!this.stickyBar) return;
    
    const scrollY = window.scrollY;
    const shouldShow = scrollY > this.heroHeight * 0.8; // Show when 80% past hero
    
    if (shouldShow && !this.isVisible) {
      this.stickyBar.style.transform = 'translateY(0)';
      this.isVisible = true;
    } else if (!shouldShow && this.isVisible) {
      this.stickyBar.style.transform = 'translateY(100%)';
      this.isVisible = false;
    }
  }

  createStickyBar() {
    const cartSummary = document.querySelector('.cart-summary');
    if (!cartSummary || this.stickyBar) return;

    this.stickyBar = document.createElement('div');
    this.stickyBar.className = 'mobile-sticky-bar';
    this.stickyBar.innerHTML = cartSummary.outerHTML;

    document.body.appendChild(this.stickyBar);
    document.body.style.paddingBottom = '120px';

    // Hide original cart summary on mobile
    cartSummary.style.display = 'none';
    
    // Re-attach event listeners to sticky bar buttons
    this.attachStickyBarListeners();
  }
  
  attachStickyBarListeners() {
    if (!this.stickyBar) return;
    
    // Get the multi-flavor selector instance
    const selector = window.multiFlavorSelector;
    if (!selector) return;
    
    // Attach listeners to sticky bar buttons
    const stickyAddToCartBtn = this.stickyBar.querySelector('[data-add-to-cart]');
    const stickyBuyNowBtn = this.stickyBar.querySelector('[data-buy-now]');
    
    if (stickyAddToCartBtn) {
      stickyAddToCartBtn.addEventListener('click', () => {
        selector.addToCart();
      });
    }
    
    if (stickyBuyNowBtn) {
      stickyBuyNowBtn.addEventListener('click', () => {
        selector.buyNow();
      });
    }
  }

  removeStickyBar() {
    if (this.stickyBar) {
      this.stickyBar.remove();
      this.stickyBar = null;
      document.body.style.paddingBottom = '';

      // Show original cart summary
      const cartSummary = document.querySelector('.cart-summary');
      if (cartSummary) {
        cartSummary.style.display = '';
      }
      
      // Remove scroll listener
      this.removeScrollListener();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.multiFlavorSelector = new MultiFlavorSelector();
  new MobileStickyBar();
});

// Handle page visibility changes to save cart state
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    const selector = window.multiFlavorSelector;
    if (selector) {
      selector.saveCartToStorage();
    }
  }
});